import { Row, Col, Select, Button, Typography, Space, message, Tag } from "antd";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

const { Option } = Select;
const { Text } = Typography;

export function DashboardFilters({ filters, onFiltersChange, data }) {
  const proyectos = data.proyectos.map((p) => ({
    value: p.codigo,
    label: p.nombre,
  }));

  const fases = Array.from(
    new Set(data.proyectos.flatMap((p) => p.fases.map((f) => f.nombre)))
  ).map((f) => ({
    value: f,
    label: f,
  }));

  const estados = [
    { value: "sobrecumplido", label: "Sobrecumplido", color: "#00E676" },
    { value: "completado", label: "Completado", color: "green" },
    { value: "en-progreso", label: "En Progreso", color: "blue" },
    { value: "en-riesgo", label: "En Riesgo", color: "orange" },
    { value: "atrasado", label: "Atrasado", color: "red" },
  ];

  const hasActiveFilters = filters.proyecto || filters.fase || filters.estado;

  const handleClearFilters = () => {
    onFiltersChange({ proyecto: "", fase: "", estado: "" });
    message.info("Filtros restablecidos");
  };

  // 🧾 Exportación Excel
  const handleExport = () => {
    let filteredTasks = data.todasLasTareas;

    if (filters.proyecto) {
      filteredTasks = filteredTasks.filter(
        (t) => t.proyectoCodigo === filters.proyecto
      );
    }
    if (filters.fase) {
      filteredTasks = filteredTasks.filter((t) => t.fase === filters.fase);
    }
    if (filters.estado && filters.estado !== "all") {
      filteredTasks = filteredTasks.filter((t) => t.estado === filters.estado);
    }

    if (filteredTasks.length === 0) {
      message.warning("No hay datos para exportar con los filtros seleccionados");
      return;
    }

    const fecha = new Date();
    const fechaFormateada = fecha.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const resumen = {
      "Fecha de exportación": fechaFormateada,
      "Proyecto seleccionado": filters.proyecto || "Todos los proyectos",
      "Fase": filters.fase || "Todas las fases",
      "Estado": filters.estado
        ? estados.find((e) => e.value === filters.estado)?.label || filters.estado
        : "Todos los estados",
      "Total tareas exportadas": filteredTasks.length,
    };

    const rows = filteredTasks.map((t) => ({
      "Código": t.codigo,
      "Descripción de la Tarea": t.descripcion?.replace(/\s+/g, " ").trim(),
      "Proyecto": t.proyecto,
      "Fase": t.fase,
      "Actividad": t.actividad || "N/A",
      "Peso": t.peso || 0,
      "Indicadores Asociados": t.indicadores?.length || 0,
      "Avance (%)": t.porcentajePromedio?.toFixed(2) || "0.00",
      "Estado": estados.find((e) => e.value === t.estado)?.label || t.estado, 
    }));

    const wb = XLSX.utils.book_new();
    const resumenData = Object.entries(resumen).map(([k, v]) => ({
      Campo: k,
      Valor: v,
    }));
    const resumenSheet = XLSX.utils.json_to_sheet(resumenData, { origin: "A2" });
    XLSX.utils.sheet_add_aoa(resumenSheet, [["📋 RESUMEN DE FILTROS APLICADOS"]], {
      origin: "A1",
    });
    XLSX.utils.book_append_sheet(wb, resumenSheet, "Resumen");

    const ws = XLSX.utils.json_to_sheet(rows, { origin: "A2" });
    XLSX.utils.sheet_add_aoa(ws, [["📌 REPORTE DETALLADO DE TAREAS"]], {
      origin: "A1",
    });

    const range = XLSX.utils.decode_range(ws["!ref"]);
    const headerRow = 2;
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRow, c: C });
      const cell = ws[cellAddress];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "317EDA" } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: {
            top: { style: "thin", color: { rgb: "CCCCCC" } },
            bottom: { style: "thin", color: { rgb: "CCCCCC" } },
          },
        };
      }
    }

    for (let R = headerRow + 1; R <= range.e.r; ++R) {
      const bg = R % 2 === 0 ? "F7F9FB" : "FFFFFF";
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) continue;
        ws[addr].s = {
          fill: { fgColor: { rgb: bg } },
          alignment: { vertical: "center", wrapText: true },
        };
      }
    }

    ws["!cols"] = Object.keys(rows[0]).map((key) => {
      if (key === "Descripción de la Tarea" || key === "Actividad")
        return { wpx: 100 };
      return {
        wch:
          Math.max(
            key.length,
            ...rows.map((r) => (r[key] ? r[key].toString().length : 0))
          ) + 2,
      };
    });

    XLSX.utils.book_append_sheet(wb, ws, "Tareas Detalladas");

    const filename = `Reporte_POA_${filters.proyecto || "General"}_${
      fecha.toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(wb, filename);
    message.success("Archivo Excel exportado correctamente");
  };

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 8,
        padding: 16,
        boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
      }}
    >
      <Row gutter={[16, 16]} align="middle" wrap>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Text strong style={{ color: "#042956" }}>
            Proyecto
          </Text>
          <Select
            value={filters.proyecto || "all"}
            onChange={(v) =>
              onFiltersChange({
                ...filters,
                proyecto: v === "all" ? "" : v,
              })
            }
            placeholder="Todos los proyectos"
            style={{ width: "100%", marginTop: 4 }}
          >
            <Option value="all">Todos los proyectos</Option>
            {proyectos.map((p) => (
              <Option key={p.value} value={p.value}>
                {p.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Text strong style={{ color: "#042956" }}>
            Fase
          </Text>
          <Select
            value={filters.fase || "all"}
            onChange={(v) =>
              onFiltersChange({
                ...filters,
                fase: v === "all" ? "" : v,
              })
            }
            placeholder="Todas las fases"
            style={{ width: "100%", marginTop: 4 }}
          >
            <Option value="all">Todas las fases</Option>
            {fases.map((f) => (
              <Option key={f.value} value={f.value}>
                {f.label}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Estado */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <Text strong style={{ color: "#042956" }}>
            Estado
          </Text>
          <Select
            value={filters.estado || "all"}
            onChange={(v) =>
              onFiltersChange({
                ...filters,
                estado: v === "all" ? "" : v,
              })
            }
            placeholder="Todos los estados"
            style={{ width: "100%", marginTop: 4 }}
          >
            <Option value="all">Todos los estados</Option>
            {estados.map((e) => (
              <Option key={e.value} value={e.value}>
                <Tag color={e.color} style={{ marginRight: 8 }}>
                  {e.label}
                </Tag>
              </Option>
            ))}
          </Select>
        </Col>
        <Col
          xs={24}
          sm={24}
          md={6}
          lg={10}
          style={{ textAlign: "right", marginTop: 28 }}
        >
          <Space wrap>
            {hasActiveFilters && (
              <Button
                icon={<ReloadOutlined />}
                onClick={handleClearFilters}
                size="middle"
              >
                Limpiar
              </Button>
            )}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              style={{
                backgroundColor: "#317EDA",
                borderColor: "#317EDA",
              }}
              onClick={handleExport}
            >
              Exportar a Excel
            </Button>
          </Space>
        </Col>
      </Row>
    </div>
  );
}
